// ─────────────────────────────────────────────────────────────────────────────
// Aterrizaje único tras el login. Ver `auth/postLoginLanding.ts` para la marca.
//
// Regla: al iniciar sesión (cualquier método) → /app/brillamos si la persona tiene campaña Brillamos
// ACTIVA, si no → /app/reconocer. Se evalúa UNA vez por login: la marca se consume al decidir, así
// que ni un F5 ni la navegación posterior vuelven a redirigir.
//
// Quien NO tiene módulo Brillamos (solo Pulso) no tiene un destino en Reconocimiento: se queda donde
// aterrizó (la home de Pulso). Frontline nunca tiene Pulso y siempre es usuario de Brillamos.
//
// La decisión reutiliza `useRecoHomeTarget` — la misma que ya usan `LandingDecision` y el guard de
// entrada de `AppLayout` — para que las tres no puedan divergir. Va como layout route SOBRE
// `PulsoLayout`/`AppLayout` y no dentro de ellos: tiene que correr antes de que alguno de los dos
// monte su vista (y dispare sus consultas), y antes del selector de `LandingDecision`.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { PageSpinner } from '@/components/common/PageSpinner';
import { hasFrontlineSession } from '@/auth/frontlineSession';
import { clearPostLogin, hasPostLoginPending } from '@/auth/postLoginLanding';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useRecoHomeTarget } from '@/hooks/useRecoHomeTarget';

/**
 * Solo se monta con la marca puesta: `useRecoHomeTarget` dispara la consulta de campaña activa, y
 * no debe hacerlo en cada carga de la app (en particular para quien entra a Pulso sin haber
 * iniciado sesión recién).
 */
function PostLoginDecision({ onDecided }: Readonly<{ onDecided: () => void }>) {
  const currentUser = useCurrentUser();
  const { recoTarget, isLoading } = useRecoHomeTarget();
  const isFrontline = hasFrontlineSession();

  // `modules` arranca en el fallback "solo Pulso" mientras el perfil carga: solo es fiable resuelto.
  const hasBrillamos = isFrontline
    || ((currentUser?.profileResolved ?? false) && (currentUser?.modules.canViewBrillamos ?? false));

  useEffect(() => {
    if (!isLoading) onDecided();
  }, [isLoading, onDecided]);

  // Nunca monta el `<Outlet/>` aquí: la vista pedida por la URL no debe llegar a pintarse (ni a
  // consultar) antes de saber a dónde va la persona. Sin Brillamos, un spinner de un solo commit y
  // `onDecided` libera el Outlet de abajo. Con Brillamos, el `Navigate` ya se ejecutó cuando el
  // efecto de `onDecided` corre (los efectos de los hijos van antes que los del padre).
  if (!isLoading && hasBrillamos) return <Navigate to={recoTarget} replace />;
  return <PageSpinner />;
}

export function PostLoginGate() {
  const [isPending, setIsPending] = useState(hasPostLoginPending);
  const handleDecided = useCallback(() => {
    clearPostLogin();
    setIsPending(false);
  }, []);

  if (!isPending) return <Outlet />;
  return <PostLoginDecision onDecided={handleDecided} />;
}

export default PostLoginGate;
